<template>
  <div>
    <v-menu
      v-model="showMenu"
      :close-on-content-click="false"
      offset-y
      max-height="400"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          v-model="searchQuery"
          placeholder="Search by email, name, phone or postal code"
          persistent-placeholder
          :loading="loading"
          @input="onSearchInput"
          @click:clear="clearSelection"
          @focus="searchQuery=''"
          v-bind="attrs"
          v-on="on"
          :append-icon="showOptions ? 'mdi-cog' : 'mdi-cog-outline'"
          @click:append="showOptions = !showOptions"
        />
        <v-chip-group>
          <v-chip outlined close small v-if="showShopify" v-on:click:close="showShopify = false; onOptionsChange()">
            Search Shopify
          </v-chip>
          <v-chip outlined close small v-if="includeDeleted"
                  v-on:click:close="includeDeleted = false; onOptionsChange()">Search Deleted
          </v-chip>
          <v-chip outlined close small v-if="autoSelectMatch"
                  v-on:click:close="autoSelectMatch = false; onOptionsChange()">Auto Select
          </v-chip>
        </v-chip-group>
      </template>

      <v-list v-if="searchResults.length > 0">
        <v-list-item
          v-for="customer in searchResults"
          :key="customer.uid"
          @click="selectCustomer(customer)"
          two-line
        >
          <v-list-item-content>
            <v-list-item-title>{{ customer.first_name }} {{ customer.last_name }}</v-list-item-title>
            <v-list-item-subtitle>
              {{ customer.email }}
              <span v-if="customer.address && customer.address.postal_code">
                · {{ customer.address.postal_code }}
              </span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Search Options Dialog -->
    <v-dialog v-model="showOptions" max-width="300">
      <v-card>
        <v-card-title class="text-h6">Search Options</v-card-title>
        <v-card-text>
          <v-checkbox
            v-model="includeDeleted"
            label="Include deleted accounts"
            hide-details
            dense
            @change="onOptionsChange"
          />
          <v-checkbox
            v-model="showShopify"
            label="Include Shopify accounts"
            hide-details
            dense
            @change="onOptionsChange"
          />
          <v-checkbox
            v-model="autoSelectMatch"
            label="Auto-select match"
            hide-details
            dense
            @change="onOptionsChange"
          />
          <v-text-field
            class="mt-4"
            :label="`Auto select time (${(autoSelectTimeout/1000).toFixed(1)} seconds)`"
            type="number"
            min="0"
            step="1000"
            v-model="autoSelectTimeout"
            @change="onOptionsChange"
            dense
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showOptions = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api';

export default {
  name: "CustomerAutocomplete",
  props: {
    value: Object
  },
  emits: ['update:uid'],
  data() {
    return {
      loading: false,
      searchResults: [],
      searchQuery: '',
      lastSearch: '',
      searchTimeout: null,
      selectedCustomer: this.value,
      showMenu: false,
      showOptions: false,
      includeDeleted: true,
      showShopify: false,
      autoSelectMatch: true,
      autoSelectTimeout: 2000
    }
  },
  mounted() {
    try {
      const item = localStorage.getItem('autocomplete-options');
      if (item) {
        const options = JSON.parse(item || {});
        const validOptions = {
          includeDeleted: options.includeDeleted !== undefined ? options.includeDeleted : this.includeDeleted,
          showShopify: options.showShopify !== undefined ? options.showShopify : this.showShopify,
          autoSelectMatch: options.autoSelectMatch !== undefined ? options.autoSelectMatch : this.autoSelectMatch,
          autoSelectTimeout: options.autoSelectTimeout !== undefined ? parseInt(options.autoSelectTimeout) : this.autoSelectTimeout
        };
        Object.assign(this, validOptions);
      }
    } catch (error) {
      console.error('failed to set options', error);
    }
  },
  methods: {
    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      if (this.lastSearch !== this.searchQuery) {
        this.searchTimeout = setTimeout(() => {
          this.searchCustomers(this.searchQuery);
          this.lastSearch = this.searchQuery;
        }, 300);
      }
    },

    async searchCustomers(query) {
      if (!query || query.length < 2) {
        this.searchResults = [];
        this.showMenu = false;
        return;
      }

      this.loading = true;
      try {
        const params = new URLSearchParams({
          query: query,
          includeDeleted: this.includeDeleted.toString(),
          skipShopify: (!this.showShopify).toString()
        });

        const response = await api.get(`v2/customers/search?${params.toString()}`);
        this.searchResults = response.data || [];
        if (this.autoSelectMatch && this.searchResults.length === 1) {
          setTimeout(() => {
            if (this.searchResults.length === 1) {
              this.selectCustomer(this.searchResults[0]);
            }
          }, this.autoSelectTimeout)
        }
        this.showMenu = this.searchResults.length > 0;
      } catch (error) {
        console.error('Error searching customers:', error);
        this.searchResults = [];
        this.showMenu = false;
      } finally {
        this.loading = false;
      }
    },

    async selectCustomer(customer) {
      const response = await api.get(`v2/customer/${customer.uid}`);
      customer = response.data;
      this.selectedCustomer = customer;
      this.searchResults = [];
      this.searchQuery = `${customer.first_name} ${customer.last_name} (${customer.email})`;
      this.lastSearch = this.searchQuery;
      this.showMenu = false;
      this.$emit('input', customer);
    },

    clearSelection() {
      this.selectedCustomer = null;
      this.$emit('input', null);
    },

    onOptionsChange() {
      // Re-run search if there's a current query to reflect new options
      if (this.searchQuery && this.searchQuery.length >= 2) {
        this.onSearchInput();
      }
      try {
        this.autoSelectTimeout = parseInt(this.autoSelectTimeout);
        const options = {
          includeDeleted: this.includeDeleted,
          showShopify: this.showShopify,
          autoSelectMatch: this.autoSelectMatch,
          autoSelectTimeout: this.autoSelectTimeout
        };
        localStorage.setItem('autocomplete-options', JSON.stringify(options));
      } catch (error) {
        console.error('failed to set options');
      }
    }
  },

  watch: {
    value(customer) {
      this.selectedCustomer = customer;
      if (this.selectedCustomer) {
        this.searchQuery = `${customer.first_name} ${customer.last_name} (${customer.email})`;
        this.lastSearch = this.searchQuery;
      } else {
        this.searchQuery = '';
      }
    }
  }
}
</script>

<style scoped>

</style>